import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { itemRollupsKeyFactory } from "@features/itemRollups";
import { WarehouseID } from "@models/Address";
import { PurchaseOrder } from "@models/PurchaseOrder";
import client, { useMutateError } from "@services/api";
import { updateWithToast } from "@services/toasts";
import { kebabCaseKeys } from "@utility/utilityFunctions";
import { buildPaginatedQuery } from "@utils/reactQuery";

export const purchaseOrdersKeyFactory = createQueryKeys("purchaseOrders", {
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: () =>
      client
        .get<PurchaseOrder>(`/purchase-orders/${id}`)
        .then((res) => res.data),
  }),
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () => client.get<PurchaseOrder[]>(`/purchase-orders`, { params }),
  }),
});

export const usePurchaseOrderQuery = (id: string | null | undefined) => {
  return useQuery({
    ...purchaseOrdersKeyFactory.detail(id!),
    enabled: !!id,
  });
};
export const usePaginatePurchaseOrdersQuery = buildPaginatedQuery(
  purchaseOrdersKeyFactory.paginated,
  {
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  }
);

type CreatePurchaseOrderPayload =
  | {
      type: "inventory";
      variantQtys: {
        id: string;
        qty: number;
      }[];
      warehouse: WarehouseID;
    }
  | {
      orderType: "pre-order" | "on-demand";
      orderVariantIds: number[];
    };

export const useCreatePurchaseOrderMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePurchaseOrderPayload) =>
      client
        .post<PurchaseOrder>("/purchase-orders", {
          __type: "purchase-order",
          ...payload,
        })
        .then((res) => res.data),
    onError: (error) => setError(error),
    onSuccess: (po) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: itemRollupsKeyFactory.paginated._def,
      });
      queryClient.setQueryData(
        purchaseOrdersKeyFactory.detail(po.id).queryKey,
        () => po
      );
    },
  });
};

type CreateFulfillmentPurchaseOrderPayload = {
  orderVariantIds: number[];
  inMarketDate: Date;
};

export const useCreateFulfillmentPurchaseOrderMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderVariantIds,
      inMarketDate,
    }: CreateFulfillmentPurchaseOrderPayload) =>
      client
        .post<PurchaseOrder>("/convert-to-fulfillment-purchase-order", {
          __type: "purchase-order",
          orderVariantIds,
          inMarketDate: inMarketDate.toISOString(),
        })
        .then((res) => res.data),
    onError: (error) => setError(error),
    onSuccess: (po) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: itemRollupsKeyFactory.paginated._def,
      });
      queryClient.setQueryData(
        purchaseOrdersKeyFactory.detail(po.id).queryKey,
        () => po
      );
    },
  });
};

type UpdatePurchaseOrderPayload = Partial<
  Omit<PurchaseOrder, "supplier"> & {
    supplierId: string;
  }
>;

export const useUpdatePurchaseOrderMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: UpdatePurchaseOrderPayload) =>
      updateWithToast(
        client
          .patch<PurchaseOrder>(`/purchase-orders/${id}`, {
            __type: "purchase-order",
            ...payload,
          })
          .then((res) => res.data)
      ),
    onError: (error) => setError(error),
    onSuccess: (po) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersKeyFactory.paginated._def,
      });
      queryClient.setQueryData(
        purchaseOrdersKeyFactory.detail(po.id).queryKey,
        () => po
      );
    },
  });
};

type PurchaseOrderActionPayload =
  | {
      action: "decline";
      declineReason: string;
      id: string;
    }
  | {
      action: "cancel";
      cancelationNote: string;
      id: string;
    }
  | {
      action: "accept" | "complete" | "ready-to-purchase" | "submit";
      id: string;
    };

export const usePurchaseOrderActionMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  // @TODO see if we can use the return value from the mutation to update the query data

  return useMutation({
    mutationFn: ({ id, action, ...payload }: PurchaseOrderActionPayload) =>
      client
        .post<PurchaseOrder>(
          `/purchase-orders/${id}/${action}`,
          kebabCaseKeys({
            ...payload,
            ...(action === "cancel" && {
              cancelationType: "order",
            }),
          }),
          { serializeBody: false }
        )
        .then((res) => res.data),
    onError: (error) => setError(error),
    onSuccess: (po) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersKeyFactory.paginated._def,
      });
      return queryClient.setQueryData(
        purchaseOrdersKeyFactory.detail(po.id).queryKey,
        () => po
      );
    },
  });
};

export const useSendChangeNotificationMutation = () => {
  const setError = useMutateError();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.post<PurchaseOrder>(`/purchase-orders/${id}/notify`),
    onError: (error) => setError(error),
  });
};

export const useDeletePurchaseOrderMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => client.delete(`/purchase-orders/${id}`),
    onError: (error) => setError(error),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrdersKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: itemRollupsKeyFactory.paginated._def,
      });
    },
  });
};
